import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, ImagePlus, Video, Globe2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VIDEO_MAX_SECONDS = 60;
const FILE_MAX_BYTES = 50 * 1024 * 1024;
const MAX_ITEMS = 10;

/**
 * Facebook-style "Create post" modal.
 *
 * Layout:
 *  ┌─────────────────────────────────────┐
 *  │ ← Create post              [Post →] │
 *  ├─────────────────────────────────────┤
 *  │ [👤] Your Name                      │
 *  │      🌐 Public                       │
 *  │                                      │
 *  │ What's on your mind, Hands?          │
 *  │ (large textarea, autosize)           │
 *  │                                      │
 *  │ ┌─ media grid (1/2/3/4+ layout) ─┐   │
 *  │ │ thumb thumb thumb [+ Add]      │   │
 *  │ └────────────────────────────────┘   │
 *  ├─────────────────────────────────────┤
 *  │ Add to your post:  [📷 Photo/video]  │
 *  └─────────────────────────────────────┘
 *
 * - Up to 10 images/videos per post (videos ≤ 60s).
 * - Caption-only posts are valid (no media required).
 * - HEIC accepted (server converts).
 */
export default function CreatePostModal({ open, onClose, onCreated, viewer }) {
  const [items, setItems] = useState([]); // [{file, blobUrl, type, duration}]
  const [caption, setCaption] = useState('');
  const [posting, setPosting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const inputRef = useRef(null);

  const reset = () => {
    items.forEach((it) => { try { URL.revokeObjectURL(it.blobUrl); } catch {} });
    setItems([]); setCaption(''); setPosting(false); setProgress({ current: 0, total: 0 });
  };

  const close = () => { reset(); onClose?.(); };

  useEffect(() => () => {
    items.forEach((it) => { try { URL.revokeObjectURL(it.blobUrl); } catch {} });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canPost = useMemo(() => (items.length > 0 || caption.trim().length > 0) && !posting, [items, caption, posting]);

  if (!open) return null;

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    if (items.length + fileList.length > MAX_ITEMS) {
      toast.error(`Up to ${MAX_ITEMS} items per post.`);
      return;
    }
    const next = [];
    for (const f of fileList) {
      const isImage = (f.type || '').startsWith('image/');
      const isVideo = (f.type || '').startsWith('video/');
      const isHeic = /\.heic$/i.test(f.name) || /\.heif$/i.test(f.name);
      if (!isImage && !isVideo && !isHeic) {
        toast.error(`Skipped: ${f.name} — unsupported file type.`);
        continue;
      }
      if (f.size > FILE_MAX_BYTES) {
        toast.error(`Skipped: ${f.name} — over 50 MB.`);
        continue;
      }
      const itemType = isVideo ? 'video' : 'image';
      const blobUrl = URL.createObjectURL(f);
      let duration = null;
      if (isVideo) {
        try {
          duration = await new Promise((res) => {
            const v = document.createElement('video');
            v.preload = 'metadata';
            v.src = blobUrl;
            v.onloadedmetadata = () => res(v.duration || null);
            v.onerror = () => res(null);
          });
          if (duration && duration > VIDEO_MAX_SECONDS) {
            toast.error(`Skipped: ${f.name} — videos must be ≤${VIDEO_MAX_SECONDS}s.`);
            URL.revokeObjectURL(blobUrl);
            continue;
          }
        } catch {}
      }
      next.push({ file: f, blobUrl, type: itemType, duration });
    }
    setItems((cur) => [...cur, ...next]);
  };

  const removeItem = (idx) => {
    setItems((cur) => {
      const it = cur[idx];
      if (it) { try { URL.revokeObjectURL(it.blobUrl); } catch {} }
      return cur.filter((_, i) => i !== idx);
    });
  };

  const submit = async () => {
    const trimmedCap = caption.trim();
    if (items.length === 0 && !trimmedCap) {
      toast.error('Write something or add a photo/video.');
      return;
    }
    setPosting(true);
    setProgress({ current: 0, total: items.length });
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    try {
      const uploaded = [];
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        const fd = new FormData();
        fd.append('file', it.file);
        // eslint-disable-next-line no-await-in-loop
        const upRes = await fetch(`${API}/media/upload?category=social`, { method: 'POST', headers, body: fd });
        // eslint-disable-next-line no-await-in-loop
        const upData = await upRes.json().catch(() => ({}));
        if (!upRes.ok) {
          toast.error(`Upload failed: ${upData.detail || it.file.name}`);
          setPosting(false);
          return;
        }
        const fullUrl = upData.url?.startsWith('http')
          ? upData.url
          : `${process.env.REACT_APP_BACKEND_URL}${upData.url}`;
        uploaded.push({
          url: fullUrl,
          type: it.type,
          duration_seconds: it.duration,
        });
        setProgress({ current: i + 1, total: items.length });
      }

      const postRes = await fetch(`${API}/social/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ media: uploaded, caption: trimmedCap }),
      });
      const postData = await postRes.json().catch(() => ({}));
      if (!postRes.ok) {
        toast.error(postData.detail || 'Could not create post');
        setPosting(false);
        return;
      }
      toast.success('Posted!');
      onCreated?.(postData.post);
      close();
    } catch {
      toast.error('Network error');
      setPosting(false);
    }
  };

  const placeholderName = (viewer?.name || '').trim().split(' ')[0] || 'friend';

  // Layout grid for the media tiles — Facebook-ish
  const gridClass =
    items.length === 1 ? 'grid grid-cols-1 gap-1' :
    items.length === 2 ? 'grid grid-cols-2 gap-1' :
    items.length === 3 ? 'grid grid-cols-3 gap-1' :
    'grid grid-cols-2 sm:grid-cols-3 gap-1';

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm" onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-xl rounded-t-3xl sm:rounded-3xl bg-[#13153A] border border-fuchsia-500/20 shadow-[0_30px_80px_-15px_rgba(217,70,239,0.45)] flex flex-col max-h-[90vh] sm:max-h-[88vh] overflow-hidden"
        data-testid="create-post-modal"
      >
        {/* Header — Facebook-style centered title */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/5">
          <button onClick={close} aria-label="Close" className="p-1 rounded-lg hover:bg-white/5 text-zinc-300" data-testid="post-close">
            <X size={18} />
          </button>
          <h2 className="text-white font-syne text-base sm:text-lg font-bold">Create post</h2>
          <button
            onClick={submit}
            disabled={!canPost}
            className={`text-sm font-bold px-3 py-1.5 rounded-full transition-all ${
              canPost
                ? 'bg-yellow-400 hover:bg-yellow-300 text-black'
                : 'bg-white/5 text-zinc-500 cursor-not-allowed'
            }`}
            data-testid="post-submit"
          >
            {posting ? (
              <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Posting…</span>
            ) : 'Post'}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
          {/* Author + audience */}
          <div className="flex items-center gap-3">
            {viewer?.profile_image ? (
              <img src={viewer.profile_image} alt="" className="w-11 h-11 rounded-full object-cover border border-white/10" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-fuchsia-500/40 to-cyan-500/40 flex items-center justify-center text-white font-bold">
                {(viewer?.name || '?')[0]}
              </div>
            )}
            <div>
              <p className="text-white font-semibold text-sm">{viewer?.name || 'You'}</p>
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-[10px] font-semibold">
                <Globe2 size={10} /> Public
              </span>
            </div>
          </div>

          {/* Caption */}
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={items.length ? 3 : 6}
            maxLength={5000}
            autoFocus
            placeholder={`What's on your mind, ${placeholderName}?`}
            className="w-full bg-transparent border-0 outline-none text-white text-lg sm:text-xl placeholder:text-zinc-500 resize-none focus:ring-0"
            data-testid="post-caption"
          />

          {/* Media — outlined "Upload Media" tile when empty, grid when populated */}
          {items.length === 0 ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full rounded-2xl border-2 border-dashed border-fuchsia-500/40 hover:border-fuchsia-400 bg-gradient-to-br from-fuchsia-500/5 to-purple-500/5 hover:from-fuchsia-500/10 hover:to-purple-500/10 transition-all flex flex-col items-center justify-center gap-2 px-6 py-8 text-zinc-300 hover:text-white"
              data-testid="post-upload-media-tile"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-fuchsia-500/30 to-purple-500/30 flex items-center justify-center">
                <ImagePlus size={20} className="text-fuchsia-300" />
              </div>
              <span className="text-sm font-bold text-white">Upload Media</span>
              <span className="text-[11px] text-zinc-400 text-center">Photos, videos (≤60s), HEIC supported · Up to {MAX_ITEMS} items</span>
            </button>
          ) : (
            <div className={`${gridClass} rounded-2xl overflow-hidden`} data-testid="post-media-grid">
              {items.map((it, idx) => (
                <div key={`${it.blobUrl}_${idx}`} className="relative bg-black aspect-square overflow-hidden group" data-testid={`post-media-tile-${idx}`}>
                  {it.type === 'image' ? (
                    <img src={it.blobUrl} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <video src={it.blobUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  )}
                  {it.type === 'video' && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] flex items-center gap-1">
                      <Video size={10} />{it.duration ? `${Math.round(it.duration)}s` : 'video'}
                    </div>
                  )}
                  <button
                    onClick={() => removeItem(idx)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 hover:bg-red-500/80 text-white flex items-center justify-center"
                    aria-label="Remove"
                    data-testid={`post-media-remove-${idx}`}
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
              {items.length < MAX_ITEMS && (
                <button
                  onClick={() => inputRef.current?.click()}
                  className="aspect-square bg-white/5 hover:bg-white/10 border-2 border-dashed border-white/15 hover:border-fuchsia-400 flex flex-col items-center justify-center gap-1 text-zinc-300 hover:text-white"
                  data-testid="post-add-more"
                >
                  <Plus size={18} />
                  <span className="text-[11px] font-semibold">Add</span>
                </button>
              )}
            </div>
          )}

          {/* Upload progress */}
          {posting && progress.total > 0 && (
            <div className="text-zinc-300 text-xs flex items-center gap-2">
              <Loader2 size={12} className="animate-spin text-fuchsia-300" />
              Uploading {progress.current} / {progress.total}…
            </div>
          )}
        </div>

        {/* Action bar — Facebook-style "Add to your post" row */}
        <div className="border-t border-white/5 px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <span className="text-zinc-300 text-xs sm:text-sm font-semibold">Add to your post</span>
          <button
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/5 text-zinc-200"
            data-testid="post-pick-files"
          >
            <span className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <ImagePlus size={14} className="text-emerald-300" />
            </span>
            <span className="text-xs font-semibold">Photo / Video</span>
          </button>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*,.heic,.heif"
          multiple
          className="hidden"
          onChange={(e) => { handleFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
          data-testid="post-file-input"
        />
      </div>
    </div>
  );
}
